import * as React from "react";
import { motion } from "framer-motion";
import { Loader } from "@/components/ui/loader";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { useParams } from "react-router";
import { useGetDocumentChronologyQuery } from "@/api/chronology";
import { RightSidebarContentType } from "@/views/chronology/components/rightSidebar/types";
import { ChronologyList } from "@/views/chronology/components/chronologyList/chronologyList";
import { RightSidebar } from "@/views/chronology/components/rightSidebar/rightSidebar";
import { ChronologyTimelinesRefresh } from "@/views/chronology/components/timelineRefresh";
import { useGetFileDetailsQuery } from "@/api/files";
import { ContextSidebar } from "@/components/feature/layout/left-sidebar/contextSidebar";
import { useToggle } from "react-use";
import { FileItem } from "@/types/fse";
import * as analytics from "@/services/analytics";
import { flatFileSystem } from "@/components/feature/layout/left-sidebar/helpers";

export const ChronologyTimelines: React.FC = () => {
  const [isRightDrawerVisible, setIsRightDrawerVisible] = React.useState(true);
  const { systemId = "", fseId = "" } = useParams<{ systemId: string; fseId: string }>();
  const [showLoading, setShowLoading] = React.useState(false);
  const [isSidebarVisible, toggleSidebarVisibility] = useToggle(true);
  const [selectedFse, setSelectedFse] = React.useState<string[]>([]);

  const { setCurrentPdfId } = useCurrentPdf();
  const toggleRightDrawerVisibility = React.useCallback(() => {
    setIsRightDrawerVisible(prev => !prev);
  }, []);
  const [rightDrawerContent, setRightDrawerContent] = React.useState<RightSidebarContentType>("pdf");

  const handleSetContentType = (contentType: RightSidebarContentType) => {
    setIsRightDrawerVisible(true);
    setRightDrawerContent(contentType);
  };

  const { data: fseDetails, error: detailsError } = useGetFileDetailsQuery({ fse_id: systemId });

  const { data, isLoading, error, refetch } = useGetDocumentChronologyQuery(systemId);

  React.useEffect(() => {
    analytics.chronologyViewed(systemId);
  }, [systemId]);

  React.useEffect(() => {
    if (fseDetails) {
      if (fseDetails.type === "folder") {
        const flatItems = flatFileSystem(fseDetails, 4, undefined, true);
        const firstFile = flatItems.find(item => item.type === "file");
        if (fseId) {
          setCurrentPdfId(fseId);
        } else if (firstFile) {
          setCurrentPdfId(firstFile.id);
        }

        setSelectedFse(fseId ? [fseId] : flatItems.map(item => item.id));
      } else if (fseDetails.type === "file") {
        setSelectedFse([systemId]);
        setCurrentPdfId(systemId);
      }
    }
  }, [systemId, setCurrentPdfId, fseDetails, setSelectedFse, fseId]);

  const onFileSelect = React.useCallback(
    (file: FileItem, isSelected: boolean) => {
      if (isSelected) {
        setSelectedFse(prev => [...prev, file.id]);
      } else {
        setSelectedFse(prev => prev.filter(id => id !== file.id));
      }
    },
    [setSelectedFse],
  );

  if (detailsError) {
    return <div>FSE with such fse id does not exist</div>;
  }

  return (
    <main className="flex flex-grow overflow-hidden">
      <ChronologyTimelinesRefresh
        setShowLoading={setShowLoading}
        showLoading={showLoading}
        error={error}
        refetch={refetch}
      />
      {fseDetails?.type === "folder" && (
        <ContextSidebar
          systemId={systemId}
          onFileSelect={onFileSelect}
          initialSelectedFiles={selectedFse ? selectedFse : undefined}
          isVisible={isSidebarVisible}
          onToggleVisibility={toggleSidebarVisibility}
          showFolders={false}
          showToggleAll={true}
        />
      )}
      <motion.div
        className="flex flex-grow flex-col overflow-x-hidden overflow-y-scroll"
        animate={{ width: isRightDrawerVisible ? "60%" : "100%" }}
        transition={{ duration: 0.3 }}
      >
        {isLoading || (showLoading && <Loader />)}
        {!!data && (
          <>
            {data.chronoentries.length ? (
              <ChronologyList
                selectedFse={selectedFse}
                documentChronology={data}
                setContentType={handleSetContentType}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-gray-500">
                There are no valid dates extracted from this document
              </div>
            )}
          </>
        )}
      </motion.div>

      <RightSidebar
        documentChronology={data}
        currentPdfId={systemId}
        isVisible={isRightDrawerVisible}
        toggleVisibility={toggleRightDrawerVisibility}
        contentType={rightDrawerContent}
        setContentType={setRightDrawerContent}
      />
    </main>
  );
};
